import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  TextField,
  Grid2,
  Avatar,
  InputAdornment,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Close, PictureAsPdf } from "@mui/icons-material";
import ImageUpload from "../../components/common/upload/ImageUpload";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import leftIcon from "../../../assets/left-svg.svg";
import rightIcon from "../../../assets/right-svg.svg";
import sendIcon from "../../../assets/Send.svg";
import commentsIcon from "../../../assets/comments.svg";
import swageazyCubeIcon from "../../../assets/swageazy-cube.svg";
import checkIcon from "../../../assets/check.png";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageAPI from "../../../api/ImageAPI";
import { useNotification } from "../../../utils/NotificationsContext";
import { observer } from "mobx-react";
import { OrderMockup } from "../../../models/Order/OrderMockup";
import { MockupComment } from "../../../models/Order/MockupComment";
import changesRequestedIcon from "../../../assets/changes-requested.svg";
import pendingIcon from "../../../assets/pending.svg";
import rejectIcon from "../../../assets/reject.png";
import userIcon from "../../../assets/user-icon.svg";
import { isPdfUrl } from "../../../utils/urlHelper";
interface MockupProps {
  mockups?: OrderMockup[];
  canDeleteMockup?: boolean;
  toggleMockupsPopup?: () => void;
  handleMockupUpload?: (product_id: number, images: string[]) => void;
  product: { id: number; name: string } | undefined;
  comments?: MockupComment[];
  updateMockupComment?: (mockupComment: MockupComment) => void;
  fetchMockupComment?: (mockupId: number) => void;
  isCommentsFetching?: boolean;
  deleteMockup?: (mockupId: number) => void;
  isMainMockupImageLoading?: boolean;
  selectedMockupId?: number;
  archived?: number;
  viewOnly?: boolean;
  dialogZIndex?: number;
  onOpenMockupGenerator?: (mockupId?: number) => void;
  embedded?: boolean;
  onNavigateToTab?: (tab: "generate" | "upload", mockupId?: number) => void;
  onEditMockup?: (mockupId: number) => void;
}

const AddEditMockups: React.FC<MockupProps> = ({
  mockups,
  canDeleteMockup,
  toggleMockupsPopup,
  handleMockupUpload,
  product,
  comments,
  updateMockupComment,
  fetchMockupComment,
  isCommentsFetching,
  deleteMockup,
  isMainMockupImageLoading,
  selectedMockupId,
  archived = 0,
  viewOnly = false,
  dialogZIndex = 2000,
  onOpenMockupGenerator,
  embedded = false,
  onNavigateToTab,
  onEditMockup,
}) => {
  const theme = useTheme();
  const [selectedImage, setSelectedImage] = useState<OrderMockup>();
  const [loading, setLoading] = useState(true);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [uploadMockupImage, setUploadMockupImage] = useState<File[]>([]);
  const { showError } = useNotification();
  const [mockupIndex, setMockupIndex] = useState(0);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [inputText, setInputText] = useState("");
  let lastPrintedDate: string | null | undefined = null;
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [canShowLastImage, setCanShowLastImage] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (mockups) {
      setSelectedImage(mockups[0]);
    }
    setLoading(false);
  }, [mockups]);

  useEffect(() => {
    setIsImageUploading(true);
    handleUpload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadMockupImage]);

  useEffect(() => {
    if (selectedImage?.id && fetchMockupComment && !viewOnly) {
      fetchMockupComment(selectedImage.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage?.id, viewOnly]);

  useEffect(() => {
    if (mockups) {
      const selectedIndex = mockups?.findIndex(
        (mockup) => mockup.id === selectedMockupId,
      );

      if (selectedIndex !== -1 && !canShowLastImage) {
        setSelectedImage(mockups[selectedIndex]);
        setMockupIndex(selectedIndex);
      } else if (mockups.length > 0 && canShowLastImage) {
        setSelectedImage(mockups[mockups.length - 1]);
        setMockupIndex(mockups.length - 1);
        thumbnailRefs.current[mockups.length - 1]?.scrollIntoView({
          behavior: "smooth",
          inline: "center",
        });
      } else if (mockups.length > 0) {
        setSelectedImage(mockups[0]);
        setMockupIndex(0);
        thumbnailRefs.current[0]?.scrollIntoView({
          behavior: "smooth",
          inline: "center",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockups, selectedMockupId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    }, 0);

    return () => clearTimeout(timeout);
  }, [comments]);

  const handleImageClick = (index: number, mockupDetails: OrderMockup) => {
    setMockupIndex(index);
    setSelectedImage(mockupDetails);
    setIsZoomed(false);
  };

  const handleLeftAndRightClick = (value: number) => {
    const diff = mockupIndex + value;
    if (mockups && diff >= mockups?.length) {
      setSelectedImage(mockups[0]);
      setMockupIndex(0);
    } else if (mockups && diff < 0) {
      setMockupIndex(mockups.length - 1);
      setSelectedImage(mockups[mockups.length - 1]);
    } else if (mockups) {
      setSelectedImage(mockups[diff]);
      setMockupIndex(diff);
    }

    setIsZoomed(false);
    thumbnailRefs.current[diff]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - container.left) / container.width) * 100;
    const y = ((e.clientY - container.top) / container.height) * 100;

    setZoomPosition({ x, y });
  };

  const handleMouseEnter = () => {
    setIsZoomed(true);
  };

  const handleMouseLeave = () => {
    setIsZoomed(false);
  };

  const handleUpload = () => {
    if (uploadMockupImage.length > 0) {
      const imageAPI = new ImageAPI();
      imageAPI
        .uploadImage(uploadMockupImage, "product-images")
        .then((uploadedUrl) => {
          handleMockupUpload!(product!.id, uploadedUrl);
          setUploadMockupImage([]);
          setCanShowLastImage(true);
        })
        .catch((err) => {
          showError(
            "Error occurred",
            err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
          );
        })
        .finally(() => setIsImageUploading(false));
    } else {
      setIsImageUploading(false);
    }
  };

  const handleSend = (inputText: string) => {
    if (inputText && selectedImage?.id) {
      updateMockupComment!({
        mockup_id: selectedImage?.id,
        comments: inputText,
      });
    } else {
      showError(
        "Error occurred",
        "Failed to fetch mockup id OR comment not found",
      );
    }
  };

  const handleRemoveMockup = () => {
    deleteMockup!(selectedImage?.id!);
    if (mockups && mockups.length > 0 && !isMainMockupImageLoading) {
      setSelectedImage(mockups![0]);
      setMockupIndex(0);
      setCanShowLastImage(false);
      thumbnailRefs.current[0]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }
  };

  const mainViewerUrl = selectedImage?.image_url;
  const mainViewerIsPdf = isPdfUrl(mainViewerUrl);

  const handleOpenGenerator = (mockupId?: number) => {
    if (typeof mockupId === "number" && onEditMockup) {
      onEditMockup(mockupId);
      return;
    }
    if (onNavigateToTab) {
      onNavigateToTab("generate", mockupId);
      return;
    }
    onOpenMockupGenerator?.(mockupId);
  };

  const embeddedGridSx = embedded
    ? { height: "100%", flex: 1, minHeight: 0, alignItems: "stretch" as const }
    : undefined;

  const embeddedColumnSx = embedded
    ? {
      display: "flex",
      flexDirection: "column" as const,
      minHeight: 0,
      flex: 1,
      height: "100%",
    }
    : undefined;

  const embeddedViewerSx = embedded
    ? { flex: 1, minHeight: 0 }
    : {
      minHeight: {
        xs: "14rem",
        md: "14rem",
        lg: "24rem",
        xl: "24rem",
      },
      maxHeight: {
        xs: "14rem",
        md: "14rem",
        lg: "24rem",
        xl: "24rem",
      },
    };

  const galleryContent = (
    <>
      <Grid2
        container
        spacing={embedded ? 3 : 2}
        sx={embeddedGridSx}
      >
        <Grid2
          sx={{
            paddingRight: {
              xs: 0,
              md: 0,
              lg: 3,
              xl: 3,
            },
            ...embeddedColumnSx,
          }}
          size={{
            xs: 12,
            sm: 12,
            md: 12,
            lg: viewOnly ? 12 : 7,
            xl: viewOnly ? 12 : 7,
          }}
        >
          {isMainMockupImageLoading ? (
            <Box
              sx={{
                background: theme.palette.customColors.lightGreyColor,
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                ...embeddedColumnSx,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  ...(embedded
                    ? { flex: 1, minHeight: 0 }
                    : { minHeight: "24rem", maxHeight: "24rem" }),
                  padding: 11.5,
                }}
              >
                <CircularProgress color="inherit" />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                background: theme.palette.customColors.lightGreyColor,
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                ...embeddedColumnSx,
              }}
            >
              <Box
                ref={containerRef}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  ...embeddedViewerSx,
                  padding: {
                    xs: 8,
                    md: 8,
                    lg: 11.5,
                    xl: 11.5,
                  },
                  cursor: mainViewerIsPdf ? "default" : "zoom-in",
                }}
                onMouseMove={
                  mainViewerIsPdf ? undefined : handleMouseMove
                }
                onMouseEnter={
                  mainViewerIsPdf ? undefined : handleMouseEnter
                }
                onMouseLeave={
                  mainViewerIsPdf ? undefined : handleMouseLeave
                }
              >
                {mockups &&
                  mockups.length > 0 &&
                  Boolean(selectedImage?.approved === 1) && (
                    <IconButton
                      sx={{
                        position: "absolute",
                        right: 15,
                        top: 5,
                        zIndex: 1,
                        cursor: "default",
                      }}
                      size="large"
                    >
                      <img srcSet={checkIcon} alt="" />
                    </IconButton>
                  )}

                {mockups &&
                  mockups.length > 0 &&
                  Boolean(selectedImage?.changes_requested === 1) && (
                    <IconButton
                      sx={{
                        position: "absolute",
                        right: 1,
                        top: 5,
                        zIndex: 1,
                        cursor: "default",
                      }}
                      size="large"
                    >
                      <img
                        width="35"
                        height="35"
                        srcSet={changesRequestedIcon}
                        alt=""
                      />
                    </IconButton>
                  )}

                {mockups &&
                  mockups.length > 0 &&
                  Boolean(selectedImage?.approved === 0) &&
                  Boolean(selectedImage?.changes_requested === 0) &&
                  Boolean(selectedImage?.is_rejected) === false && (
                    <IconButton
                      sx={{
                        position: "absolute",
                        right: 1,
                        top: 5,
                        zIndex: 1,
                        cursor: "default",
                      }}
                      size="large"
                    >
                      <img
                        width="35"
                        height="35"
                        srcSet={pendingIcon}
                        alt="Pending"
                      />
                    </IconButton>
                  )}

                {mockups &&
                  mockups.length > 0 &&
                  Boolean(selectedImage?.is_rejected) === true && (
                    <IconButton
                      sx={{
                        position: "absolute",
                        right: 1,
                        top: 5,
                        zIndex: 1,
                        cursor: "default",
                      }}
                      size="large"
                    >
                      <img
                        width="30"
                        height="30"
                        srcSet={rejectIcon}
                        alt="Rejected"
                      />
                    </IconButton>
                  )}

                {mockups && mockups?.length > 1 && (
                  <IconButton
                    sx={{ position: "absolute", left: 0, zIndex: 1 }}
                    size="large"
                    onClick={() => handleLeftAndRightClick(-1)}
                  >
                    <img srcSet={leftIcon} alt="" />
                  </IconButton>
                )}
                {mockups && mockups?.length > 0 && (
                  <>
                    {mainViewerIsPdf && mainViewerUrl ? (
                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 0,
                          width: "100%",
                          height: "100%",
                          ...(embedded
                            ? { minHeight: 0, flex: 1 }
                            : {
                              minHeight:
                                window.innerWidth >= 1200
                                  ? "20rem"
                                  : "10rem",
                              maxHeight:
                                window.innerWidth >= 1200
                                  ? "20rem"
                                  : "10rem",
                            }),
                        }}
                      >
                        <Box
                          component="iframe"
                          src={mainViewerUrl}
                          title="Mockup PDF"
                          sx={{
                            width: "100%",
                            height: "100%",
                            ...(embedded
                              ? { minHeight: 0 }
                              : {
                                minHeight:
                                  window.innerWidth >= 1200
                                    ? "20rem"
                                    : "10rem",
                              }),
                            border: 0,
                            display: "block",
                            bgcolor:
                              theme.palette.customColors.white,
                          }}
                        />
                      </Box>
                    ) : (
                      <img
                        ref={imageRef}
                        src={mainViewerUrl}
                        alt="Product"
                        style={{
                          height: "100%",
                          width: "100%",
                          objectFit: "contain",
                          ...(embedded
                            ? { minHeight: 0, maxHeight: "100%" }
                            : {
                              minHeight:
                                window.innerWidth >= 1200
                                  ? "20rem"
                                  : "10rem",
                              maxHeight:
                                window.innerWidth >= 1200
                                  ? "20rem"
                                  : "10rem",
                            }),
                          transition:
                            "transform 0.3s ease-in-out",
                          transform: isZoomed
                            ? "scale(2)"
                            : "scale(1)",
                          transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                        }}
                      />
                    )}
                  </>
                )}
                {mockups && mockups?.length === 0 && (
                  <CustomTypography variant="heading5">
                    NO MOCKUPS FOUND
                  </CustomTypography>
                )}
                {mockups && mockups?.length > 1 && (
                  <IconButton
                    sx={{ position: "absolute", right: 0, zIndex: 1 }}
                    size="large"
                    onClick={() => handleLeftAndRightClick(1)}
                  >
                    <img srcSet={rightIcon} alt="" />
                  </IconButton>
                )}
                {mockups && mockups?.length > 0 && !viewOnly && (
                  <>
                    {(onEditMockup || onNavigateToTab || onOpenMockupGenerator) &&
                      selectedImage?.id && (
                        <IconButton
                          sx={{
                            position: "absolute",
                            right: 55,
                            bottom: 20,
                            zIndex: 1,
                            backgroundColor:
                              theme.palette.customColors.darkBlue,
                            width: 30,
                            height: 30,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors.info,
                              opacity: 0.9,
                            },
                            "&.Mui-disabled": {
                              opacity: 0.5,
                            },
                          }}
                          onClick={() => handleOpenGenerator(selectedImage.id)}
                          disabled={archived === 1}
                          aria-label="Edit mockup"
                        >
                          <EditOutlinedIcon
                            sx={{
                              color: theme.palette.customColors.white,
                              width: 18,
                              height: 18,
                            }}
                          />
                        </IconButton>
                      )}
                    <IconButton
                      color="secondary"
                      sx={{
                        position: "absolute",
                        right: 15,
                        bottom: 20,
                        zIndex: 1,
                        backgroundColor:
                          theme.palette.customColors.pinkLight,
                        width: 30,
                        height: 30,
                        "&.Mui-disabled": {
                          opacity: 0.5,
                        },
                      }}
                      onClick={handleRemoveMockup}
                      disabled={archived === 1 || !canDeleteMockup}
                    >
                      <DeleteIcon
                        sx={{
                          color: theme.palette.customColors.pinkDark,
                          width: 20,
                          height: 20,
                        }}
                      />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 2,
              overflowX: "auto",
              paddingBottom: 1,
              paddingRight: 1,
              flexShrink: 0,
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor:
                  theme.palette.customColors.greyBackground,
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor:
                  theme.palette.customColors.greyDarkborder,
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor:
                    theme.palette.customColors.greyTextColor,
                },
              },
            }}
          >
            {mockups?.map((mockup: any, index: any) => (
              <Box
                sx={{
                  background:
                    selectedImage?.id === mockup.id
                      ? theme.palette.customColors.lightPurple
                      : theme.palette.customColors.white,
                  borderRadius: "8px",
                  border: `2px solid ${selectedImage?.id === mockup.id
                    ? theme.palette.customColors.purpleMain
                    : theme.palette.customColors.greyBorder
                    }`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: theme.palette.customColors.purpleMain,
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 12px ${theme.palette.customColors.purpleMain}30`,
                  },
                }}
                key={index}
              >
                <Box
                  key={index}
                  sx={{
                    minWidth: "5rem",
                    maxWidth: "5rem",
                    minHeight: "5rem",
                    maxHeight: "5rem",
                    height: "5rem",
                    overflow: "hidden",
                    cursor: "pointer",
                    flexShrink: 0,
                    padding: 1,
                  }}
                  onClick={() => handleImageClick(index, mockup)}
                  ref={(el: HTMLDivElement | null) =>
                    (thumbnailRefs.current[index] = el)
                  }
                >
                  {isPdfUrl(mockup.image_url) ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor:
                          theme.palette.customColors.lightGreyColor,
                      }}
                    >
                      <PictureAsPdf
                        sx={{
                          fontSize: "2.25rem",
                          color: theme.palette.error.main,
                          opacity: 0.9,
                        }}
                      />
                    </Box>
                  ) : (
                    <img
                      src={mockup.image_url}
                      alt={`Thumbnail ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Grid2>

        {!viewOnly && (
          <Grid2
            sx={{
              paddingLeft: {
                xs: 0,
                md: 0,
                lg: 2,
                xl: 2,
              },
              ...embeddedColumnSx,
            }}
            size={{ xs: 12, sm: 12, md: 12, lg: 5, xl: 5 }}
          >
            <Box sx={embeddedColumnSx}>
              {(selectedImage?.approved === 0 ||
                selectedImage === undefined ||
                selectedImage === null) &&
                Boolean(selectedImage?.is_rejected) === false && (
                  <>
                    {!onOpenMockupGenerator && !onNavigateToTab && (
                      isImageUploading ? (
                        <Box
                          sx={{
                            mt: 4,
                            mb: 7.8,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {" "}
                          <CircularProgress color="inherit" />{" "}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            mt: {
                              xs: 0,
                              md: 0,
                              lg: -2.5,
                              xl: -2.5,
                            },
                            mb: 2,
                          }}
                        >
                          <ImageUpload
                            disabled={archived === 1}
                            id={String(product?.id) || ""}
                            size={"large"}
                            showImagesList={false}
                            multiple={true}
                            existingImageList={[]}
                            addImagesToBeUploaded={(file) => {
                              setUploadMockupImage(file);
                            }}
                          />
                        </Box>
                      )
                    )}
                  </>
                )}
              <Box
                sx={
                  embedded
                    ? {
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minHeight: 0,
                    }
                    : undefined
                }
              >
                <Box
                  sx={{
                    borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
                    display: "flex",
                    alignItems: "center",
                    paddingTop: 0,
                    paddingBottom: 1,
                    mb: 2,
                    flexShrink: 0,
                  }}
                >
                  <IconButton>
                    <img srcSet={commentsIcon} alt="" />
                  </IconButton>
                  <CustomTypography variant="heading6" fontWeight={600}>
                    Comments
                  </CustomTypography>
                </Box>

                {isCommentsFetching ? (
                  <Box
                    sx={{
                      ...(embedded
                        ? {
                          flex: 1,
                          minHeight: 0,
                        }
                        : {
                          minHeight: {
                            xs: "10rem",
                            md: "10rem",
                            lg: "15.3rem",
                            xl: "15.3rem",
                          },
                          maxHeight: {
                            xs: "10rem",
                            md: "10rem",
                            lg: "15.3rem",
                            xl: "15.3rem",
                          },
                        }),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress color="inherit" />
                  </Box>
                ) : (
                  <>
                    {comments && comments?.length > 0 ? (
                      <Box
                        sx={{
                          ...(embedded
                            ? {
                              flex: 1,
                              minHeight: 0,
                            }
                            : {
                              minHeight: {
                                xs: "10rem",
                                md: "10rem",
                                lg: "14.5rem",
                                xl: "14.5rem",
                              },
                              maxHeight: {
                                xs: "10rem",
                                md: "10rem",
                                lg: "14.5rem",
                                xl: "14.5rem",
                              },
                            }),
                          overflowX: "hidden",
                          overflowY: "auto",
                          mt: 1,
                          mb: 1.5,
                        }}
                        ref={chatContainerRef}
                      >
                        {comments.map((comment, index) => {
                          const currentDate = comment.created_date
                            ? new Intl.DateTimeFormat("en-GB", {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }).format(new Date(comment.created_date))
                            : null;
                          const showDate =
                            currentDate !== lastPrintedDate;

                          if (showDate) {
                            lastPrintedDate = currentDate;
                          }
                          return (
                            <Box key={index}>
                              {showDate && currentDate && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    paddingBottom: 1,
                                    paddingTop: 1,
                                  }}
                                >
                                  <CustomTypography
                                    variant="body3"
                                    color={
                                      theme.palette.customColors
                                        .greyTextColor
                                    }
                                  >
                                    {currentDate}
                                  </CustomTypography>
                                </Box>
                              )}

                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 2,
                                  maxHeight: "13.2rem",
                                  overflowY: "auto",
                                  pb: 0.2,
                                  pt: 0.2,
                                }}
                              >
                                {comment.user_id !== null &&
                                  comment.user_id !== 0 && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "flex-start",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{ display: "flex", gap: 1 }}
                                      >
                                        <Avatar
                                          src={userIcon}
                                          alt="User Avatar"
                                          sx={{ width: 25, height: 25 }}
                                        />
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              p: 1,
                                              backgroundColor:
                                                theme.palette.customColors
                                                  .grayBackGroundColor,
                                              borderRadius: "0.5rem",
                                              maxWidth: "fit-content",
                                            }}
                                          >
                                            <CustomTypography variant="body2">
                                              {comment.comment}
                                            </CustomTypography>
                                          </Box>
                                        </Box>
                                      </Box>
                                    </Box>
                                  )}

                                {comment.admin_id !== null &&
                                  comment.admin_id !== 0 && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        gap: 1,
                                        bottom: 0,
                                        pb: 0.2,
                                        pt: 0.2,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          p: 1,
                                          backgroundColor:
                                            theme.palette.customColors
                                              .lightBlue,
                                          borderRadius: "0.5rem",
                                          maxWidth: "fit-content",
                                        }}
                                      >
                                        <CustomTypography variant="body2">
                                          {comment.comment}
                                        </CustomTypography>
                                      </Box>
                                      <Avatar
                                        src={swageazyCubeIcon}
                                        alt="System Avatar"
                                        sx={{ width: 25, height: 25 }}
                                      />
                                    </Box>
                                  )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                          ...(embedded
                            ? { flex: 1, minHeight: 0 }
                            : {
                              minHeight: {
                                xs: "10rem",
                                md: "10rem",
                                lg: "15.25rem",
                                xl: "15.25rem",
                              },
                              maxHeight: {
                                xs: "10rem",
                                md: "10rem",
                                lg: "15.25rem",
                                xl: "15.25rem",
                              },
                            }),
                        }}
                      >
                        <CustomTypography
                          variant="body2"
                          fontWeight={600}
                        >
                          NO COMMENTS
                        </CustomTypography>
                      </Box>
                    )}
                  </>
                )}

                {(selectedImage?.approved === 0 ||
                  selectedImage === undefined ||
                  selectedImage === null) &&
                  Boolean(selectedImage?.is_rejected) === false && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mt: 1,
                        gap: 1,
                        borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
                        pt: 1,
                        flexShrink: 0,
                      }}
                    >
                      <TextField
                        multiline={true}
                        maxRows={2}
                        sx={{
                          marginTop: "0.2rem",
                          fontSize: "0.5rem",
                          background: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.customColors.greyBorder}`,
                          borderRadius: "8px",
                          outline: "none",
                          "& .MuiInputBase-input": {
                            padding: "0.6rem",
                            overflow: "scroll",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": {
                              display: "none",
                            },
                          },
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#2A2A2A"
                              : theme.palette.customColors.white,
                        }}
                        fullWidth
                        placeholder="Type your message..."
                        value={inputText}
                        disabled={archived === 1}
                        onChange={(e) => {
                          const value = e.target.value;
                          setInputText(value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSend(inputText);
                            setInputText("");
                          }
                        }}
                        slotProps={{
                          htmlInput: {
                            style: { fontSize: "0.875rem" },
                            maxLength: 200,
                          },
                          input: {
                            style: {
                              fontSize: "0.875rem",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#2A2A2A"
                                  : theme.palette.customColors.white,
                              padding: "0.25rem",
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  color="primary"
                                  disabled={inputText?.length === 0}
                                  onClick={() => {
                                    handleSend(inputText);
                                    setInputText("");
                                  }}
                                >
                                  <img srcSet={sendIcon} alt="" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Box>
                  )}
                {onOpenMockupGenerator && !onNavigateToTab &&
                  (selectedImage?.approved === 0 ||
                    selectedImage === undefined ||
                    selectedImage === null) &&
                  Boolean(selectedImage?.is_rejected) === false && (
                    <Box sx={{ mt: 2 }}>
                      <AppButton
                        label="Add mockup"
                        variant="primary"
                        onClick={() => handleOpenGenerator()}
                        disabled={archived === 1}
                        customStyles={{ width: "100%" }}
                      />
                      <CustomTypography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mt: 1,
                          textAlign: "center",
                        }}
                      >
                        Generate or upload a new mockup
                      </CustomTypography>
                    </Box>
                  )}
              </Box>
            </Box>
          </Grid2>
        )}
      </Grid2>

      {viewOnly && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 2,
            pt: 1,
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <AppButton
            variant="grayscale"
            label="Close"
            onClick={() => toggleMockupsPopup?.()}
          />
        </Box>
      )}
    </>
  );

  return (
    <>
      {loading ? (
        <CustomTypography variant="heading4">...Loading</CustomTypography>
      ) : embedded ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            minHeight: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {galleryContent}
        </Box>
      ) : (
        <Dialog
          open={true}
          maxWidth="lg"
          fullWidth
          slotProps={{
            transition: {
              timeout: 300,
              sx: {
                backgroundColor: theme.palette.customColors.backdrop,
                backdropFilter: "blur(4px)",
              },
            },
            backdrop: {
              timeout: 300,
              sx: {
                backgroundColor: theme.palette.customColors.backdrop,
                backdropFilter: "blur(4px)",
              },
            },
            paper: {
              sx: {
                borderRadius: "16px",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
                overflow: "hidden",
                maxHeight: "90vh",
                maxWidth: "53.125rem",
                width: "100%",
                margin: { xs: "16px", sm: "32px" },
                background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
              },
            },
          }}
          sx={{
            zIndex: dialogZIndex,
            "& .MuiDialog-container": {
              alignItems: "center",
              justifyContent: "center",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
                borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
              }}
            >
              <CustomTypography variant="heading3">
                {product!.name || "Product Mockups"}
              </CustomTypography>
              <IconButton
                onClick={() => toggleMockupsPopup?.()}
                sx={{
                  color: theme.palette.customColors.greyTextColor,
                  "&:hover": {
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                    color: theme.palette.customColors.blackBorder,
                  },
                }}
              >
                <Close />
              </IconButton>
            </Box>
            <DialogContent
              sx={{
                padding: { xs: "20px", sm: "24px" },
                flex: 1,
                overflow: "auto",
                maxHeight: "70vh",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: theme.palette.customColors.greyBackground,
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.customColors.greyDarkborder,
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: theme.palette.customColors.greyTextColor,
                  },
                },
              }}
            >
              {galleryContent}
            </DialogContent>
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default observer(AddEditMockups);
